import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { TrendingUp, Gauge, TrendingDown, Loader2, Lock, MessageCircle, Wallet, Crown, Building2 } from 'lucide-react';
import { Modal } from '../Modal';
import { CategoryNavigationButtons } from '../../game/CategoryNavigationButtons';
import { ArchiveButton } from '../../game/ArchiveButton';
import { Button } from '../Button';
import { ShareButton } from '../ShareButton';
import { UserModal } from './UserModal';
import { StatsModal } from './StatsModal';
import { DiscussionModal } from './DiscussionModal';
import { UpgradePromptModal } from './UpgradePromptModal';
import { ProgressModal } from './ProgressModal';
import { CompanyInfoModal } from './CompanyInfoModal';
import { OpenPositionForm } from '../../game/OpenPositionForm';
import { AvatarSprite } from '../../avatar/AvatarSprite';
import { GameState } from '../../../types/game';
import { StockCategoryFilter } from '../../../types/stockCategories';
import {
  SentimentVoteOption,
  SENTIMENT_OPTIONS
} from '../../../types/sentiment';
import { useAuth } from '../../../hooks/useAuth';
import { usePremium } from '../../../hooks/usePremium';
import { useSentimentVoting } from '../../../hooks/useSentimentVoting';
import { useTopGuesses } from '../../../hooks/useTopGuesses';
import { useBalance } from '../../../hooks/usePortfolio';
import { useCompanyInfo } from '../../../hooks/useCompanyInfo';
import { SentimentBar } from '../../game/SentimentBar';
import { FamiliarityPoll } from '../../game/FamiliarityPoll';
import { cn } from '../../../utils/cn';
import confetti from 'canvas-confetti';
import { buildShareLine } from '../../game/ShareEmoji';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { formatTimerDisplay } from '../../../utils/timeFormatting';
import { getCollectiblesForDate } from '../../../config/collectiblesConfig';
import { getCurrentGameTimestamp } from '../../../config/dateTimeConfig';

export interface FinishedModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameState: GameState;
  onShare: () => void;
  category?: StockCategoryFilter;
  className?: string;
  isReplayMode?: boolean;
  onAnalyze?: () => void;
  progressRefreshKey?: number;
  autoOpenProgress?: boolean;
}

const SENTIMENT_BUTTON_META: Record<SentimentVoteOption, {
  label: string;
  description: string;
  icon: typeof TrendingUp;
  classes: string;
  activeClasses: string;
}> = {
  buy: {
    label: 'Buy',
    description: 'Bullish call',
    icon: TrendingUp,
    classes: 'border-emerald-500/60 text-emerald-600 dark:text-emerald-200 hover:bg-emerald-500/10 focus:ring-emerald-500/30',
    activeClasses: 'bg-emerald-500/15 border-emerald-500 text-emerald-600 dark:text-emerald-100'
  },
  hold: {
    label: 'Hold',
    description: 'Stay steady',
    icon: Gauge,
    classes: 'border-amber-500/60 text-amber-600 dark:text-amber-200 hover:bg-amber-500/10 focus:ring-amber-500/30',
    activeClasses: 'bg-amber-500/15 border-amber-500 text-amber-600 dark:text-amber-100'
  },
  sell: {
    label: 'Sell',
    description: 'Bearish tilt',
    icon: TrendingDown,
    classes: 'border-rose-500/60 text-rose-500 dark:text-rose-200 hover:bg-rose-500/10 focus:ring-rose-500/30',
    activeClasses: 'bg-rose-500/15 border-rose-500 text-rose-500 dark:text-rose-100'
  }
};

const WALLSTREETBETS_SENTIMENT_BUTTON_META: typeof SENTIMENT_BUTTON_META = {
  buy: {
    ...SENTIMENT_BUTTON_META.buy,
    label: 'PUMP',
    description: 'Send it to the moon'
  },
  hold: {
    ...SENTIMENT_BUTTON_META.hold,
    label: 'HODL',
    description: 'Diamond hands engage'
  },
  sell: {
    ...SENTIMENT_BUTTON_META.sell,
    label: 'DUMP',
    description: 'Paper hands tap out'
  }
};

/**
 * FinishedModal - overlays the game board once a round is concluded.
 * Presents win/lose summary, sharing controls, and community sentiment voting.
 */
export const FinishedModal: React.FC<FinishedModalProps> = ({
  isOpen,
  onClose,
  gameState,
  onShare,
  category,
  className,
  isReplayMode = false,
  onAnalyze,
  progressRefreshKey,
  autoOpenProgress = false
}) => {
  const isWallstreetbetsCategory = category === 'wallstreetbets';
  const sentimentButtonMeta = isWallstreetbetsCategory
    ? WALLSTREETBETS_SENTIMENT_BUTTON_META
    : SENTIMENT_BUTTON_META;

  const { user } = useAuth();
  const { isPremium } = usePremium();
  // TODO: balance data temporarily removed - will be used for coin display feature
  const { refetch: refetchBalance } = useBalance({ enabled: false });
  const [pendingVote, setPendingVote] = useState<SentimentVoteOption | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressSource, setProgressSource] = useState<'auto' | 'manual'>('auto');
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const autoFlowDecidedRef = useRef(false);

  const hasCollectibleEvent = !!getCollectiblesForDate(getCurrentGameTimestamp());

  // Prefetch Company Info availability once the game is finished. If the
  // cache returns null/404 (no AI content for today), we suppress the modal
  // entirely — Progress's "Next" button will close to Results instead.
  const companyInfoQuery = useCompanyInfo({
    category: category ?? 'popular',
    gameDate: gameState.gameDate,
    enabled: isOpen && gameState.gameStatus !== 'playing' && !!category,
  });
  const companyInfoAvailable = !!companyInfoQuery.data;

  // Auto-open post-game flow on first game completion (not on re-open via "Results" button).
  // Flow: Progress Tracker first → click "Next" → Company Info (if available) → close to Results.
  // For anonymous users, only auto-open on wins — or on collectible event days.
  useEffect(() => {
    if (!isOpen) {
      setShowProgressModal(false);
      setShowCompanyInfo(false);
      autoFlowDecidedRef.current = false;
      return;
    }

    if (!autoOpenProgress) return;
    if (!(user || gameState.gameStatus === 'won' || hasCollectibleEvent)) return;
    if (autoFlowDecidedRef.current) return;

    setProgressSource('auto');
    setShowProgressModal(true);
    autoFlowDecidedRef.current = true;
  }, [
    isOpen,
    autoOpenProgress,
    user,
    gameState.gameStatus,
    hasCollectibleEvent,
  ]);

  // Progress "Next" → arm Company Info eagerly. The render-side gate
  // (`showCompanyInfo && companyInfoAvailable`) keeps it hidden if the
  // backend has no AI content cached, and opens it as soon as an in-flight
  // fetch resolves — so timing of the click doesn't strand the modal.
  const handleProgressNext = () => {
    setShowProgressModal(false);
    if (progressSource === 'auto') {
      setShowCompanyInfo(true);
    }
  };

  // Progress × / backdrop → close, no chain.
  const handleProgressClose = () => {
    setShowProgressModal(false);
  };

  // Company Info is the final step in the auto chain — close goes back to Results.
  const handleCompanyInfoClose = () => {
    setShowCompanyInfo(false);
  };

  const handleManualProgress = () => {
    setProgressSource('manual');
    setShowProgressModal(true);
  };

  const handleManualCompanyInfo = () => {
    setShowCompanyInfo(true);
  };
  const [confettiSettled, setConfettiSettled] = useState(false);

  // Accessibility detection
  const prefersReducedMotion = usePrefersReducedMotion();

  const isAuthenticated = Boolean(user);
  const symbol = gameState.solution?.Symbol ?? null;

  const enableSentiment = isOpen && gameState.gameStatus !== 'playing' && !!symbol && confettiSettled;

  const {
    sentiment,
    isLoading: sentimentLoading,
    isVoting,
    error: sentimentError,
    hasVoted,
    userVote,
    submitVote
  } = useSentimentVoting({
    symbol,
    category,
    date: gameState.gameDate,
    enabled: enableSentiment,
    isAuthenticated
  });

  // Fetch top guesses for the finished modal
  const {
    topGuesses,
    isLoading: topGuessesLoading
  } = useTopGuesses({
    category: category || 'spy',
    gameDate: gameState.gameDate,
    // Correctness is computed from this day's solution, never from the RPC.
    solution: gameState.solution,
    enabled: isOpen && gameState.gameStatus !== 'playing' && confettiSettled
  });

  // Confetti state and effect - must be before early return (Rules of Hooks)
  const attempts = gameState.guesses.length;
  const isWin = gameState.gameStatus === 'won';

  // Calculate coins earned based on attempts and win/loss
  const calculateCoinsEarned = (attempts: number, isWin: boolean): number => {
    if (!isWin) return 10; // Failed game

    switch (attempts) {
      case 1: return 1000;
      case 2: return 500;
      case 3: return 250;
      case 4: return 100;
      default: return 10; // 5+ attempts = minimal reward
    }
  };

  const coinsEarned = useMemo(
    () => calculateCoinsEarned(attempts, isWin),
    [attempts, isWin]
  );

  // Generate emoji share line for visual display
  const emojiLine = isWin
    ? buildShareLine({ winAt: attempts, category })
    : buildShareLine({ fail: true, category });

  // Party popper confetti effect - dual burst from bottom corners
  useEffect(() => {
    if (isOpen && isWin && !prefersReducedMotion) {
      const defaults = {
        spread: 55,
        ticks: 100,
        gravity: 1,
        decay: 0.94,
        startVelocity: 30,
        colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42', '#ffa62d', '#ff36ff']
      };

      // Left cannon
      confetti({
        ...defaults,
        particleCount: 40,
        origin: { x: 0, y: 1 },
        angle: 60
      });

      // Right cannon
      confetti({
        ...defaults,
        particleCount: 40,
        origin: { x: 1, y: 1 },
        angle: 120
      });
    }
  }, [isOpen, isWin, prefersReducedMotion]);

  // Delay data fetching until confetti animation settles to prevent frame drops
  useEffect(() => {
    if (isOpen && isWin) {
      setConfettiSettled(false);
      const timer = setTimeout(() => setConfettiSettled(true), 800);
      return () => clearTimeout(timer);
    } else if (isOpen && !isWin) {
      // No confetti on loss, enable features immediately
      setConfettiSettled(true);
    } else if (!isOpen) {
      setConfettiSettled(false);
    }
  }, [isOpen, isWin]);

  // Refetch balance when modal opens after game completion
  // This ensures fresh coin balance is shown immediately
  useEffect(() => {
    if (isOpen && gameState.gameStatus !== 'playing' && user && confettiSettled) {
      // Refetch balance to ensure fresh data after game completion
      // This handles race condition where modal opens before
      // backend submission completes and invalidates queries
      refetchBalance();
    }
  }, [isOpen, gameState.gameStatus, user, confettiSettled, refetchBalance]);

  if (!isOpen) {
    return null;
  }

  const disableButtons = hasVoted || isVoting;

  const voteSummaryText = 'Vote now';

  const handleVoteClick = (option: SentimentVoteOption) => {
    if (disableButtons) return;
    handleVote(option);
  };

  const handleVote = async (option: SentimentVoteOption) => {
    if (disableButtons) return;
    setPendingVote(option);
    try {
      await submitVote(option, 0);
    } catch (error) {
      // Error feedback handled in hook
    } finally {
      setPendingVote(null);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-[calc(100vw-2rem)] sm:max-w-md md:max-w-lg lg:max-w-2xl max-h-[60vh] sm:max-h-[75vh] md:max-h-[80vh] overflow-y-auto mb-28 sm:mb-24"
        closeOnOverlayClick
        showCloseButton={false}
      >
        <div className={cn('relative pt-2 sm:pt-3 md:pt-4 px-3 sm:px-4 md:px-6 pb-16 sm:pb-4 md:pb-6', className)}>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <span className="text-xl leading-none">×</span>
          </button>
          <div className="space-y-4">
            <div className="text-center space-y-2">
              {isWin ? (
                <>
                  <h2 className="text-xl font-semibold text-green-600 dark:text-green-400">
                    Congratulations! 🎉
                  </h2>
                <p className="text-base font-medium text-foreground">
                  You guessed {gameState.solution?.Security} correctly!
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-red-600 dark:text-red-400">
                  {isWallstreetbetsCategory ? 'Guh' : 'Game Over'}
                </h2>
                <p className="text-lg text-foreground">
                  The answer was: <strong>{gameState.solution?.Security}</strong>
                </p>

                <p className="text-sm text-muted-foreground">Better luck tomorrow!</p>
              </>
            )}
            </div>
            {isWin && (
              <div className="rounded-lg bg-green-50 dark:bg-green-900/30 p-4 text-center space-y-3">
                <p className="text-base font-medium text-foreground">
                  Solved in {attempts} attempt{attempts !== 1 ? 's' : ''}
                  {gameState.timerResult?.wasStarted && (
                    <> • Time: {formatTimerDisplay(gameState.timerResult.elapsedMs)}</>
                  )}
                </p>

                <p className="text-base font-mono tracking-wider text-foreground whitespace-pre-line">
                  {emojiLine}
                </p>
              </div>
            )}

            {!isWin && (
              <div className="flex justify-center">
                <div className="text-lg sm:text-xl font-mono tracking-wider whitespace-pre-line">
                  {emojiLine}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <div className="flex justify-center">
                <ShareButton
                  onShare={onShare}
                  className="w-full sm:w-auto bg-green-300 hover:bg-green-400 text-black border-none"
                />
              </div>

              {/* Baron Wuffett Analysis CTA */}
              {confettiSettled && (() => {
                const canAccess = category === 'popular' || isPremium;
                return (
                  <div className="flex justify-center">
                    <button
                      onClick={() => canAccess ? onAnalyze?.() : setShowUpgradeModal(true)}
                      className="w-full rounded-xl bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500
                                 text-black font-bold py-3 px-6
                                 hover:from-yellow-400 hover:via-amber-300 hover:to-yellow-400
                                 transition-colors relative overflow-hidden"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent
                                        -translate-x-full animate-shimmer" />
                      {!canAccess && <Crown className="absolute top-1.5 right-2.5 w-4 h-4 text-amber-700 z-10" />}
                      <span className="relative flex items-center gap-2 justify-center">
                        <AvatarSprite gesture="baron-wuffett-smile" size={40} className="shrink-0" />
                        Analyze this stock with Baron Wuffett
                      </span>
                    </button>
                  </div>
                );
              })()}

              {/* Manual reopen for Company Info — only shown if AI content is cached for today */}
              {companyInfoAvailable && (
                <div className="flex justify-center">
                  <button
                    onClick={handleManualCompanyInfo}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    Company info
                  </button>
                </div>
              )}

              {/* Manual reopen for Analysis Tracker */}
              {(user || isWin || hasCollectibleEvent) && (
                <div className="flex justify-center">
                  <button
                    onClick={handleManualProgress}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
                  >
                    View Progress Tracker
                  </button>
                </div>
              )}
            </div>

            {/* Most Common Guesses Section */}
            {topGuesses.length > 0 && (
              <div className="space-y-3 border-t border-border/60 pt-6 text-left">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Most Common Guesses
                  </p>
                </div>
                {topGuessesLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {topGuesses.map((guess, index) => {
                      const isCorrect = guess.isCorrect;
                      return (
                        <div
                          key={guess.guess_text}
                          className={cn(
                            'flex items-center justify-between rounded-lg px-3 py-2 text-sm',
                            isCorrect
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          )}
                        >
                          <span className="font-medium">
                            {index + 1}. {guess.guess_text}
                          </span>
                          <span className="text-xs opacity-75">
                            {guess.guess_percentage}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Company Familiarity Poll */}
            {enableSentiment && (
              <FamiliarityPoll
                symbol={symbol!}
                companyName={gameState.solution?.Security}
                category={category}
                gameDate={gameState.gameDate}
              />
            )}

            {enableSentiment && (
              <div className="space-y-5 border-t border-border/60 pt-6 text-left">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Community Sentiment
                  </p>
                  <h3 className="text-lg font-semibold text-foreground mt-1">
                    How would you trade {'$'}{gameState.solution?.Symbol} today?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cast your vote to see how the community feels about {gameState.solution?.Security}.
                  </p>
                </div>

                <SentimentBar
                  sentiment={sentiment}
                  highlightedOption={userVote}
                  isLoading={sentimentLoading || isVoting}
                  showResults={hasVoted}
                />

                <div className="space-y-3">
                  <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                    <span className="text-sm font-medium text-muted-foreground">
                      {voteSummaryText}
                    </span>
                    {hasVoted && userVote && (
                      <span className="text-sm font-semibold text-foreground">
                        Thanks for voting {sentimentButtonMeta[userVote].label}!
                      </span>
                    )}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3">
                    {SENTIMENT_OPTIONS.map((option) => {
                      const meta = sentimentButtonMeta[option];
                      const Icon = meta.icon;
                      const isActive = userVote === option;
                      const showLoader = isVoting && pendingVote === option;
                      // Allow anonymous voting on popular category
                      const canVoteAnonymously = !isAuthenticated && category === 'popular';
                      const showLock = !isAuthenticated && !canVoteAnonymously;

                      return (
                        <Button
                          key={option}
                          type="button"
                          variant="outline"
                          disabled={disableButtons}
                          onClick={() => {
                            if (showLock) {
                              setShowUserModal(true);
                            } else {
                              handleVoteClick(option);
                            }
                          }}
                          className={cn(
                            'flex h-full flex-col items-start gap-1 rounded-xl border-2 px-4 py-3 text-left transition-all duration-200 sm:items-center sm:text-center',
                            meta.classes,
                            isActive && meta.activeClasses,
                            disableButtons && !isActive && 'opacity-70'
                          )}
                          aria-label={`Vote ${meta.label}`}
                        >
                          <div className="flex w-full items-center justify-between gap-2 sm:justify-center">
                            <span className="flex items-center gap-2">
                              {showLoader ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : showLock ? (
                                <Lock className="h-4 w-4" />
                              ) : (
                                <Icon className="h-4 w-4" />
                              )}
                              <span className="font-semibold">{meta.label}</span>
                            </span>
                            <span className="text-xs text-muted-foreground sm:hidden">{meta.description}</span>
                          </div>
                          <span className="hidden text-xs text-muted-foreground sm:block">{meta.description}</span>
                        </Button>
                      );
                    })}
                  </div>

                  {!isAuthenticated && category !== 'popular' && (
                    <p className="text-xs text-muted-foreground">
                      Sign in to see what others voted.
                    </p>
                  )}

                  {sentimentError && (
                    <p className="text-xs text-destructive">
                      {sentimentError}
                    </p>
                  )}

                  {/* Track Sentiment Vote History Button */}
                  <div className="mt-4 pt-2 border-t border-border/30">
                    <Button
                      type="button"
                      onClick={() => setShowStatsModal(true)}
                      className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white"
                      aria-label="View sentiment vote history statistics"
                    >
                      Track your Sentiment Vote History Stats
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Coins Earned - shown for both win and loss, hidden in replay mode */}
            {!isReplayMode && (
              <div className="flex flex-col items-center justify-center gap-1 py-2 text-base border-t border-border/60 pt-6">
                <span className="inline-flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span className="font-semibold text-green-700 dark:text-green-300 inline-flex items-baseline gap-1 text-sm sm:text-base">
                    <span>Earned +{coinsEarned.toLocaleString()} coins!</span>
                    <sup className="text-[0.55rem] font-semibold text-green-600 dark:text-green-300">BETA</sup>
                  </span>
                </span>
                <span className="text-xs text-muted-foreground mt-1">
                  (You can use these coins to invest in your Paper Portfolio)
                </span>
                {!isAuthenticated && (
                  <span className="text-[0.6rem] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mt-1">
                    Sign in to collect.
                  </span>
                )}
              </div>
            )}

            {/* Paper Trading Section - hidden in replay mode */}
            {enableSentiment && !isReplayMode && (
              <>
                {/* Show locked preview when not authenticated OR hasn't voted */}
                {(!isAuthenticated || !hasVoted) && (
                  <div className="space-y-3 border-t border-border/60 pt-6 text-left">
                    {/* Header matching OpenPositionForm */}
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Paper Trade
                      </p>
                      <sup className="text-[0.55rem] font-semibold text-muted-foreground">BETA</sup>
                    </div>

                    {/* Locked state box */}
                    <div className="rounded-lg bg-muted/50 p-4 opacity-70">
                      <div className="flex items-center gap-3 mb-3">
                        <Lock className="h-5 w-5 text-foreground" />
                        <p className="text-sm font-medium text-foreground">
                          Vote on sentiment to <span className="font-bold">use your coins</span> and track stocks in your paper portfolio
                        </p>
                      </div>

                      {/* Preview of Long/Short options */}
                      <div className="flex gap-2 mt-3">
                        <div className="flex-1 rounded-lg bg-emerald-500/20 border border-emerald-500/50 p-3 text-center opacity-70">
                          <TrendingUp className="h-4 w-4 text-emerald-600 mx-auto mb-1" />
                          <span className="text-xs font-bold text-emerald-600">LONG</span>
                          <p className="text-[10px] text-muted-foreground mt-1">Vote Buy</p>
                        </div>
                        <div className="flex-1 rounded-lg bg-rose-500/20 border border-rose-500/50 p-3 text-center opacity-70">
                          <TrendingDown className="h-4 w-4 text-rose-600 mx-auto mb-1" />
                          <span className="text-xs font-bold text-rose-600">SHORT</span>
                          <p className="text-[10px] text-muted-foreground mt-1">Vote Sell</p>
                        </div>
                      </div>

                      {/* Hold explanation */}
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        Voting Hold means you can't open a position on this stock.
                      </p>
                    </div>
                  </div>
                )}

                {/* Show actual form when authenticated AND has voted */}
                {isAuthenticated && hasVoted && (
                  <OpenPositionForm
                    stockSymbol={gameState.solution?.Symbol || ''}
                    category={category || 'popular'}
                    gameDate={gameState.gameDate}
                    userVote={userVote}
                  />
                )}
              </>
            )}

            {/* Discussion Button */}
            <div className="mt-6 pt-6 border-t border-border/60">
              <Button
                type="button"
                onClick={() => setShowDiscussionModal(true)}
                className="w-full bg-[#FF4500] hover:bg-[#e03d00] text-white border-none"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                View Discussion
              </Button>
            </div>

            {/* Feedback Link */}
            <div className="mt-4 text-center">
              <a
                href="/feedback"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Have feedback? Click here.
              </a>
            </div>
          </div>
        </div>
      </Modal>

      {/* Floating Category Navigation - portaled to appear above the modal backdrop */}
      {category && isOpen && !showProgressModal && !showCompanyInfo && ReactDOM.createPortal(
        <div className="fixed inset-x-0 bottom-0 z-[2147483003] flex justify-center pointer-events-none pb-2 sm:pb-4">
          <div className="w-full max-w-[calc(100vw-2rem)] sm:max-w-md md:max-w-lg lg:max-w-2xl mx-2 sm:mx-4 pointer-events-auto space-y-2">
            <ArchiveButton category={category} />
            <CategoryNavigationButtons
              currentCategory={category}
              showMessage={false}
            />
          </div>
        </div>,
        document.body
      )}

      <UserModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
      />

      <StatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        category={category}
        userId={user?.id}
        showCategoryContext={true}
      />

      <DiscussionModal
        isOpen={showDiscussionModal}
        onClose={() => setShowDiscussionModal(false)}
        stockSymbol={gameState.solution?.Symbol || null}
        category={category}
        gameDate={gameState.gameDate}
      />

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Avatar Stock Analysis"
        message="Upgrade to Premium to unlock Baron Wuffett's stock analysis for all categories."
        requiresPremium={true}
        feature="Avatar Stock Analysis"
      />

      <ProgressModal
        isOpen={showProgressModal}
        onClose={handleProgressClose}
        onContinue={handleProgressNext}
        refreshKey={progressRefreshKey}
        currentSymbol={gameState.solution?.Symbol}
        didWin={isWin}
        category={category}
      />

      <CompanyInfoModal
        isOpen={showCompanyInfo && companyInfoAvailable}
        onClose={handleCompanyInfoClose}
        onNext={handleCompanyInfoClose}
        data={companyInfoQuery.data}
      />

    </>
  );
};
export default FinishedModal;
